import { useState, useEffect, useCallback } from 'react';
import { casesApi } from '../lib/api';
import { Button } from './ui/button';
import { 
    Camera, 
    Upload, 
    Trash2, 
    X,
    ZoomIn,
    ChevronLeft,
    ChevronRight,
    Download
} from 'lucide-react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import DragDropUpload from './DragDropUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Lightbox per visualizzare foto ingrandite
function PhotoLightbox({ photos, currentIndex, onClose, onNext, onPrev, onDownload, onDelete, canDelete }) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') onNext();
            if (e.key === 'ArrowLeft') onPrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, onNext, onPrev]);

    const currentPhoto = photos[currentIndex];
    if (!currentPhoto) return null;

    return (
        <div 
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={(e) => {
                // Chiudi solo se il click è direttamente sull'overlay
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            {/* Pulsanti top right */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-[110]">
                {/* Pulsante elimina (solo se canDelete) */}
                {canDelete && (
                    <button type="button"
                        onClick={(e) => { 
                            e.stopPropagation();
                            e.preventDefault();
                            onDelete(currentPhoto); 
                        }}
                        className="p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors"
                        title="Elimina foto"
                        data-testid="lightbox-delete-photo-btn"
                    >
                        <Trash2 className="w-6 h-6 text-red-500" />
                    </button>
                )}
                {/* Pulsante download */}
                <button type="button"
                    onClick={(e) => { 
                        e.stopPropagation(); 
                        onDownload(currentPhoto); 
                    }}
                    className="p-2 bg-[#00FF41]/20 hover:bg-[#00FF41]/30 rounded-full transition-colors"
                    title="Scarica foto"
                    data-testid="lightbox-download-photo-btn"
                >
                    <Download className="w-6 h-6 text-[#00FF41]" />
                </button>
                {/* Pulsante chiudi */}
                <button type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                    data-testid="lightbox-close-btn"
                >
                    <X className="w-6 h-6 text-white" />
                </button>
            </div>
            
            {/* Navigazione */}
            {photos.length > 1 && (
                <>
                    <button type="button"
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-[110]"
                    >
                        <ChevronLeft className="w-8 h-8 text-white" />
                    </button>
                    <button type="button"
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-[110]"
                    >
                        <ChevronRight className="w-8 h-8 text-white" />
                    </button>
                </>
            )}
            
            {/* Immagine */}
            <div 
                className="max-w-[90vw] max-h-[90vh] flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={currentPhoto.url}
                    alt={currentPhoto.original_filename}
                    className="max-w-full max-h-[80vh] object-contain"
                />
                <div className="mt-4 text-center">
                    <p className="text-white/80">{currentPhoto.original_filename}</p>
                    {photos.length > 1 && (
                        <p className="text-white/40 text-sm mt-1">
                            {currentIndex + 1} / {photos.length}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function InvestigatedPhotosManager({ caseId, caseData, onUpdate, isAdmin, showUpload = true }) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    
    // Stato per il dialog di conferma eliminazione
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [photoToDelete, setPhotoToDelete] = useState(null);

    const token = localStorage.getItem('token');

    // Carica le foto come blob URLs
    useEffect(() => {
        const loadPhotos = async () => {
            setLoading(true);
            const loadedPhotos = [];
            
            // Usa investigated_photos se disponibile, altrimenti fallback a investigated_photo singola
            const photosToLoad = caseData.investigated_photos?.length > 0 
                ? caseData.investigated_photos 
                : (caseData.investigated_photo ? [caseData.investigated_photo] : []);
            
            for (const photo of photosToLoad) {
                try {
                    const response = await axios.get(
                        `${API_URL}/cases/${caseId}/investigated-photo/${photo.id}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob'
                        }
                    );
                    loadedPhotos.push({
                        ...photo,
                        url: URL.createObjectURL(response.data)
                    });
                } catch (err) {
                    // Silent fail for photo loading errors
                }
            }
            
            setPhotos(loadedPhotos);
            setLoading(false);
        };

        loadPhotos();

        return () => {
            // Cleanup blob URLs
            photos.forEach(p => p.url && URL.revokeObjectURL(p.url));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, caseData.investigated_photos, caseData.investigated_photo, token]);

    // Gestisce upload da file input o drag and drop
    const handleUploadFiles = async (files) => {
        if (!files || files.length === 0) return;
        
        const scrollPos = window.scrollY;
        
        try {
            setUploading(true);
            setError('');
            
            for (const file of Array.from(files)) {
                await casesApi.uploadInvestigatedPhoto(caseId, file);
            }
            
            await onUpdate();
            // Aumentato timeout per permettere re-render completo
            setTimeout(() => window.scrollTo(0, scrollPos), 150);
        } catch (err) {
            setError('Errore nel caricamento delle foto');
        } finally {
            setUploading(false);
        }
    };

    const handleUpload = async (e) => {
        const files = e.target.files;
        handleUploadFiles(files);
    };

    // Funzione per aprire il dialog di conferma
    const confirmDelete = useCallback((photo) => {
        setPhotoToDelete(photo);
        setDeleteDialogOpen(true);
    }, []);

    // Funzione che esegue effettivamente l'eliminazione
    const executeDelete = useCallback(async () => {
        if (!photoToDelete) return;
        
        const photoId = photoToDelete.id;
        
        try {
            await axios.delete(
                `${API_URL}/cases/${caseId}/investigated-photo?photo_id=${photoId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Rimuovi la foto dallo stato locale
            setPhotos(prevPhotos => {
                const newPhotos = prevPhotos.filter(p => p.id !== photoId);
                // Revoke URL della foto eliminata
                const deletedPhoto = prevPhotos.find(p => p.id === photoId);
                if (deletedPhoto?.url) {
                    URL.revokeObjectURL(deletedPhoto.url);
                }
                
                // Se non ci sono più foto, chiudi il lightbox
                if (newPhotos.length === 0) {
                    setLightboxOpen(false);
                } else if (lightboxIndex >= newPhotos.length) {
                    // Se l'indice è fuori range, vai all'ultima foto
                    setLightboxIndex(newPhotos.length - 1);
                }
                
                return newPhotos;
            });
            
            // Reset stato eliminazione
            setPhotoToDelete(null);
            
            // Notifica il parent affinché ricarichi i dati del caso
            // (parent in EditCaseModal aggiorna solo localCaseData, non chiude il modal)
            if (onUpdate) {
                try { await onUpdate(); } catch { /* no-op */ }
            }
            
        } catch (err) {
            setError('Errore nell\'eliminazione della foto');
        }
    }, [photoToDelete, caseId, token, lightboxIndex, onUpdate]);

    const handleDeleteFromLightbox = useCallback((photo) => {
        confirmDelete(photo);
    }, [confirmDelete]);

    const handleDownload = useCallback((photo) => {
        // Crea un link per scaricare la foto
        const link = document.createElement('a');
        link.href = photo.url;
        link.download = photo.original_filename || `foto_indagato_${photo.id}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const openLightbox = useCallback((index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    }, []);

    const nextPhoto = useCallback(() => {
        setLightboxIndex((prev) => (prev + 1) % photos.length);
    }, [photos.length]);

    const prevPhoto = useCallback(() => {
        setLightboxIndex((prev) => (prev - 1 + photos.length) % photos.length);
    }, [photos.length]);

    const closeLightbox = useCallback(() => {
        setLightboxOpen(false);
    }, []);

    return (
        <div className="space-y-3">
            {error && (
                <div className="bg-red-500/10 border border-red-500 p-2 text-red-500 text-sm">
                    {error}
                </div>
            )}
            
            {/* Griglia foto con Drag & Drop */}
            <DragDropUpload
                onFilesSelected={handleUploadFiles}
                onError={(msg) => setError(msg)}
                accept="image/*"
                multiple={true}
                disabled={uploading || !isAdmin || !showUpload}
                compact={true}
                className="rounded-none"
            >
                <div className="flex flex-wrap gap-3 p-2 min-h-[104px] border border-dashed border-white/20 hover:border-[#00FF41]/50 transition-colors">
                    {loading ? (
                        <div className="w-24 h-24 border border-white/20 flex items-center justify-center bg-black/30">
                            <div className="w-6 h-6 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                        </div>
                    ) : photos.length > 0 ? (
                        <>
                            {photos.map((photo, index) => (
                                <div 
                                    key={photo.id} 
                                    className="relative group w-24 h-24 border border-white/20 overflow-hidden cursor-pointer"
                                    onClick={(e) => { e.stopPropagation(); openLightbox(index); }}
                                >
                                    <img
                                        src={photo.url}
                                        alt={photo.original_filename}
                                        className="w-full h-full object-cover"
                                    />
                                    
                                    {/* Overlay con zoom */}
                                    <div 
                                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-none"
                                    >
                                        <ZoomIn className="w-6 h-6 text-white" />
                                    </div>
                                    
                                    {/* Pulsante elimina su hover (solo admin + showUpload) */}
                                    {isAdmin && showUpload && (
                                        <button type="button"
                                            onClick={(e) => { e.stopPropagation(); confirmDelete(photo); }}
                                            data-testid={`delete-investigated-photo-${photo.id}`}
                                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10 hover:bg-red-600"
                                            title="Elimina foto"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    )}

                                    {/* Pulsante download su hover */}
                                    <button type="button"
                                        onClick={(e) => { e.stopPropagation(); handleDownload(photo); }}
                                        className="absolute -bottom-2 -right-2 p-1 bg-[#00FF41] text-black rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                        title="Scarica foto"
                                    >
                                        <Download className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                            
                            {/* Pulsante + per aggiungere altre foto */}
                            {isAdmin && showUpload && (
                                <div 
                                    className="w-24 h-24 border border-dashed border-[#00FF41]/50 flex flex-col items-center justify-center bg-black/30 hover:border-[#00FF41] hover:bg-[#00FF41]/5 transition-all"
                                >
                                    {uploading ? (
                                        <div className="w-6 h-6 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Upload className="w-5 h-5 text-[#00FF41] mb-1" />
                                            <span className="text-[10px] text-[#00FF41]">AGGIUNGI</span>
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full flex flex-col items-center justify-center py-4 text-center">
                            <Camera className="w-8 h-8 text-white/30 mb-2" />
                            {isAdmin && showUpload ? (
                                <p className="text-white/40 text-xs">Trascina qui le foto o clicca per selezionare</p>
                            ) : (
                                <p className="text-white/40 text-xs">Nessuna foto</p>
                            )}
                        </div>
                    )}
                </div>
            </DragDropUpload>
            
            {photos.length > 0 && (
                <p className="text-white/40 text-xs mt-2">
                    {photos.length} foto • Clicca per ingrandire • Hover per scaricare
                </p>
            )}
            
            {/* Lightbox */}
            {lightboxOpen && photos.length > 0 && (
                <PhotoLightbox
                    photos={photos}
                    currentIndex={lightboxIndex}
                    onClose={closeLightbox}
                    onNext={nextPhoto}
                    onPrev={prevPhoto}
                    onDownload={handleDownload}
                    onDelete={handleDeleteFromLightbox}
                    canDelete={isAdmin && showUpload}
                />
            )}
            
            {/* Dialog di conferma eliminazione */}
            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Elimina foto"
                description="Sei sicuro di voler eliminare questa foto? L'azione è irreversibile."
                confirmText="Elimina"
                cancelText="Annulla"
                onConfirm={executeDelete}
                variant="destructive"
            />
        </div>
    );
}
